import { Component, computed, effect, inject, input, linkedSignal, signal } from '@angular/core';
import { FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DokaButton, DokaModal, DokaModalController, DokaNumberInput } from '@doka/angular-core';
import { I18NextEagerPipe, I18NextService } from 'angular-i18next';
import { firstValueFrom } from 'rxjs';
import {
  CreateFiscalYearModel,
  ICreateFiscalYearModel,
  IFiscalYearCountryModel,
  PlansClient,
} from 'src/api/services/generated/api-clients';
import { CreateFiscalYearFormType } from 'src/app/master-data/plans/create-fiscal-year-slide-over/create-fiscal-year.form.type';
import { InputDatePickerComponent } from '../../../forms/input-date-picker.component';
import { ToastService } from 'src/app/shared/services/toast.service';
import { SpinnerComponent } from '../../../spinner/spinner.component';
import {
  CountryCheckboxListComponent,
  CountryCheckboxOption,
} from 'src/app/shared/components/country-checkbox-list/country-checkbox-list.component';

@Component({
  selector: 'app-create-fiscal-year-dialog',
  templateUrl: './create-fiscal-year-dialog.component.html',
  imports: [
    DokaModal,
    DokaButton,
    I18NextEagerPipe,
    InputDatePickerComponent,
    DokaNumberInput,
    ReactiveFormsModule,
    SpinnerComponent,
    CountryCheckboxListComponent,
  ],
})
export class CreateFiscalYearDialogComponent {
  private readonly modalCtrl = inject(DokaModalController);
  private readonly plansClient = inject(PlansClient);
  private readonly toastService = inject(ToastService);
  private readonly i18n = inject(I18NextService);
  private readonly fb = inject(NonNullableFormBuilder);

  public readonly isYearReadonly = signal(false);
  public readonly countryId = input<string>();
  public readonly divisionId = input<string>();
  public readonly latestYear = input.required<number>();
  public readonly titleKey = input('Dashboard.FiscalYearDialog.Title');
  public readonly subtitleKey = input('Dashboard.FiscalYearDialog.Subtitle');
  /** When set, the fiscal year is only applied to the countries selected in the dialog. */
  public readonly countries = input<IFiscalYearCountryModel[]>();

  protected readonly isLoading = signal(false);

  protected readonly countryOptions = computed<CountryCheckboxOption[]>(() =>
    (this.countries() ?? []).map((country) => ({
      id: country.countryId,
      label: country.name,
      locked: country.isPlanned,
      description: country.isPlanned ? this.i18n.t('MasterData.PlansGrid.AlreadyPlanned') : null,
    })),
  );

  protected readonly selectedCountryIds = linkedSignal<ReadonlySet<string>>(
    () =>
      new Set(
        (this.countries() ?? []).filter((country) => !country.isPlanned).map((country) => country.countryId),
      ),
  );

  protected readonly hasCountrySelection = computed(() => !!this.countries());

  protected form: FormGroup<CreateFiscalYearFormType> = this.fb.group({
    year: this.fb.control(0, [Validators.required, Validators.min(1)]),
    latestPlanSubmissionDate: this.fb.control(new Date(), [Validators.required]),
  });

  protected latestYearHasChanged = effect(() => {
    const fiscalYear = this.isYearReadonly() ? this.latestYear() : this.latestYear() + 1;

    this.form.controls.year.setValue(fiscalYear);
    this.form.controls.year.setValidators([Validators.required, Validators.min(fiscalYear)]);
    this.form.controls.latestPlanSubmissionDate.setValue(new Date(fiscalYear, 11, 31));
  });

  protected closeModal(): void {
    this.modalCtrl.dismiss();
  }

  protected async submit() {
    try {
      this.isLoading.set(true);
      const body: ICreateFiscalYearModel = {
        countryId: this.countryId() ?? null,
        divisionId: this.divisionId() ?? null,
        countryIds: this.hasCountrySelection() ? [...this.selectedCountryIds()] : null,
        latestPlanSubmissionDate: this.form.controls.latestPlanSubmissionDate.value,
        year: this.form.controls.year.value,
      };

      await firstValueFrom(this.plansClient.createFiscalYear(new CreateFiscalYearModel(body)));

      this.isLoading.set(false);

      this.modalCtrl.dismiss(body.year, 'created');
    } catch (error) {
      console.error('Error creating fiscal year', error);
      this.toastService.showError(this.i18n.t('Dashboard.FiscalYearDialog.CreateError'));

      this.modalCtrl.dismiss(false, 'error');
    } finally {
      this.isLoading.set(false);
    }
  }
}
